export const WEBSITE_SECTION_LABELS = Object.freeze({
  hero: "Hero",
  about: "About",
  gallery: "Gallery",
  facilities: "Facilities",
  grounds: "Grounds",
  pricing: "Pricing",
  booking: "Booking",
  location: "Location",
  policies: "Policies",
  contact: "Contact",
  stats: "Statistics",
  testimonials: "Testimonials",
  faqs: "FAQs",
  app: "App Promotion",
});

export const DEFAULT_WEBSITE_SECTION_ORDER = Object.freeze([
  "hero",
  "about",
  "gallery",
  "facilities",
  "grounds",
  "pricing",
  "booking",
  "location",
  "policies",
  "contact",
]);

export const WEBSITE_SECTION_KEYS = Object.freeze([
  ...DEFAULT_WEBSITE_SECTION_ORDER,
  "stats",
  "testimonials",
  "faqs",
  "app",
]);

const createSectionOptions = (options) =>
  Object.freeze(
    (options || DEFAULT_WEBSITE_SECTION_ORDER).map((option) => {
      const normalized =
        typeof option === "string"
          ? {
              key: option,
              label: WEBSITE_SECTION_LABELS[option],
            }
          : option;

      return Object.freeze({ ...normalized });
    }),
  );

export const isWebsiteSectionVisible = (website, key) =>
  website?.sections?.[key] !== false;

const createControls = (overrides = {}) =>
  Object.freeze({
    primaryColor: true,
    accentColor: true,
    backgroundStyle: true,
    heroHeadline: true,
    heroSubheadline: true,
    ctaLabel: true,
    bookingSlotSize: false,
    bookingOptions: true,
    sections: true,
    seo: true,
    ...overrides,
  });

const createAdvancedConfiguration = (overrides = {}) =>
  Object.freeze({
    enabled: false,
    general: false,
    copyFields: Object.freeze([]),
    testimonials: false,
    faqs: false,
    stats: false,
    social: false,
    app: false,
    ...overrides,
  });

const createThemeDefinition = ({
  id,
  name,
  description,
  defaultPalette,
  controls,
  advancedConfiguration,
  sectionOptions,
  fallbackNotes = {},
}) =>
  Object.freeze({
    id,
    name,
    description,

    defaultPalette: Object.freeze({
      primaryColor: "#33A444",
      accentColor: "#111827",
      backgroundStyle: "clean",
      ...defaultPalette,
    }),

    controls: createControls(controls),

    sectionOptions: createSectionOptions(sectionOptions),

    advancedConfiguration:
      createAdvancedConfiguration(
        advancedConfiguration,
      ),

    fallbackNotes: Object.freeze({
      ...fallbackNotes,
    }),
  });

const ARENA_COPY_FIELDS = Object.freeze([
  "hoursPrefix",
  "hoursSuffix",
  "heroBadge",
  "groundsEyebrow",
  "groundsTitle",
  "groundsDescription",
  "groundCapacitySuffix",
  "pricePrefix",
  "priceOnRequestLabel",
  "groundBookLabel",
  "emptyGroundsLabel",
  "bookingEyebrow",
  "bookingTitle",
  "bookingDescription",
  "bookingStepOneTitle",
  "bookingStepOneDescription",
  "bookingStepTwoTitle",
  "bookingStepTwoDescription",
  "bookingStepThreeTitle",
  "bookingStepThreeDescription",
  "bookingButtonLabel",
  "testimonialsEyebrow",
  "testimonialsTitle",
  "aboutEyebrow",
  "aboutTitle",
  "locationEyebrow",
  "locationContactLabel",
  "contactEyebrow",
  "contactTitle",
  "contactDescription",
  "contactOpenLabel",
  "contactOpenDescription",
  "faqEyebrow",
  "faqTitle",
  "footerQuickLinksTitle",
  "footerBookLabel",
  "footerAboutLabel",
  "footerContactLabel",
  "footerStartBookingLabel",
  "footerVisitTitle",
  "footerCopyrightSuffix",
  "footerPoweredByText",
]);

const LEGACY_THEME_DEFINITIONS = [
  {
    id: "classic",
    name: "Classic",
    description:
      "Elegant, traditional and professional.",
  },
  {
    id: "modern",
    name: "Modern",
    description:
      "Bold cards, gradients and contemporary styling.",
  },
  {
    id: "sports",
    name: "Sports",
    description:
      "Energetic, action-focused and stadium inspired.",
  },
  {
    id: "minimal",
    name: "Minimal",
    description:
      "Clean typography with generous white space.",
  },
  {
    id: "dark",
    name: "Dark",
    description:
      "Premium dark appearance with strong contrast.",
  },
].map((definition) =>
  createThemeDefinition(definition),
);

const arenaDefinition = createThemeDefinition({
  id: "arena",
  name: "Arena",
  description:
    "Premium sports landing page with rich imagery and booking-focused sections.",

  defaultPalette: {
    primaryColor: "#33A444",
    accentColor: "#111827",
    backgroundStyle: "clean",
  },

  advancedConfiguration: {
    enabled: true,
    general: true,
    copyFields: ARENA_COPY_FIELDS,
    testimonials: true,
    faqs: true,
    stats: true,
    social: true,
    app: true,
  },

  fallbackNotes: {
    heroHeadline:
      "Uses the venue name from the API, then the exact original Arena headline.",

    heroSubheadline:
      "Uses the venue short description from the API, then the exact original Arena description.",

    advancedConfiguration:
      "Website Configuration overrides take priority. Venue and ground API data is used where applicable, then the exact original Arena text, lists and bundled images remain visible. Disable a supported section to hide it.",

    copyField:
      "Leave empty to use API-backed venue data where applicable, then the exact original Arena text.",

    heroBadge:
      "Uses the exact original Arena badge when left empty.",

    contactEmail:
      "Uses the venue contact email from the API, then the original Arena contact email.",

    rating:
      "Uses the exact original Arena rating when no positive configured value is provided.",

    reviewCount:
      "Uses the exact original Arena review count when no positive configured value is provided.",

    testimonials:
      "Uses the exact original Arena testimonials when the configured list is empty.",

    faqs:
      "Uses the exact original Arena FAQs when the configured list is empty.",

    stats:
      "Uses the exact original Arena statistics when the configured list is empty.",

    social:
      "Configured social links take priority; existing Onground social-link fallbacks remain unchanged.",

    app:
      "The Show app promotion setting controls visibility. When enabled, blank app fields use the exact original Arena promotion copy.",
  },
});

const boxArenaDefinition =
  createThemeDefinition({
    id: "box-arena",
    name: "Box Arena",
    description:
      "Bold multi-sport layout with dynamic venue and ground information.",

    defaultPalette: {
      primaryColor: "#39FF14",
      accentColor: "#0A0A0A",
      backgroundStyle: "clean",
    },

    controls: {
      /*
       * These controls are currently not consumed by
       * BoxArenaTheme and must not be presented as
       * functioning controls in Phase 2.
       */
      backgroundStyle: false,
      heroHeadline: false,
      sections: true,
    },

    sectionOptions: [
      "hero",
      "grounds",
      "stats",
      "about",
      "booking",
      "testimonials",
      "faqs",
      "app",
      "contact",
    ],

    advancedConfiguration: {
      enabled: true,
      general: false,
      copyFields: Object.freeze([
        "heroBadge",
      ]),
      testimonials: true,
      faqs: true,
      stats: true,
      social: true,
      app: true,
    },

    fallbackNotes: {
      primaryColor:
        "Uses the original Box Arena neon green when reset to the theme default.",

      accentColor:
        "Uses the original Box Arena black palette when reset to the theme default.",

      heroHeadline:
        "The current Box Arena headline is part of the fixed visual design.",

      heroSubheadline:
        "Uses the venue short description, then the original Box Arena description.",

      advancedConfiguration:
        "Only settings already connected to Box Arena are shown. Hidden Arena settings remain saved and unchanged.",

      copyField:
        "Leave empty to use the original Box Arena theme fallback.",

      heroBadge:
        "Uses the venue city, country and name when left empty.",

      testimonials:
        "Uses the original Box Arena testimonials when the list is empty.",

      faqs:
        "Uses the original Box Arena FAQs when the list is empty.",

      stats:
        "Uses the original Box Arena statistics when the list is empty.",

      social:
        "Each Box Arena footer social link is hidden until a valid URL is configured.",

      app:
        "Uses configured store links and the Onground website fallback without changing the Box Arena layout.",

      backgroundStyle:
        "Box Arena does not currently implement Clean, Sport or Premium background variants.",

      sections:
        "Controls the supported Box Arena sections without changing their original order.",
    },
  });

const picklePointDefinition =
  createThemeDefinition({
    id: "pickle-point-nepal",
    name: "Pickle Point Nepal",
    description:
      "Editorial pickleball landing page with dynamic venue, court and contact information.",

    defaultPalette: {
      primaryColor: "#FF5A3C",
      accentColor: "#181310",
      backgroundStyle: "clean",
    },

    controls: {
      primaryColor: true,
      accentColor: true,
      backgroundStyle: false,
      heroHeadline: false,
      heroSubheadline: false,
      ctaLabel: false,
      sections: true,
    },

    sectionOptions: [
      "hero",
      "about",
      "stats",
      "grounds",
      "testimonials",
      "faqs",
      {
        key: "contact",
        label: "Contact & Map",
      },
      "app",
    ],

    advancedConfiguration: {
      enabled: true,
      general: false,
      copyFields: Object.freeze([
        "heroTag",
        "aboutTitle",
        "courtsTitle",
        "contactTitle",
      ]),
      testimonials: true,
      faqs: true,
      stats: true,
      social: true,
      app: true,
    },

    fallbackNotes: {
      advancedConfiguration:
        "Empty Pickle Point fields use venue and ground API data where available, then the exact original static text and bundled assets.",

      copyField:
        "Leave empty to preserve the API-driven value or the exact original Pickle Point fallback.",

      testimonials:
        "Uses the original Pickle Point testimonials when the list is empty.",

      faqs:
        "Uses the original Pickle Point FAQs when the list is empty.",

      stats:
        "Uses venue and ground data where available, then the original Pickle Point statistics.",

      backgroundStyle:
        "Pickle Point preserves its original cream, sand, coral and charcoal visual design.",

      sections:
        "Controls the supported Pickle Point sections without changing their original order.",
    },
  });


const merlionDefinition =
  createThemeDefinition({
    id: "merlion-gymnasium",
    name: "Merlion Gymnasium",
    description:
      "Premium indoor multi-sport layout with API-driven courts, venue details and booking actions.",

    defaultPalette: {
      primaryColor: "#CC2936",
      accentColor: "#1A1A1A",
      backgroundStyle: "clean",
    },

    controls: {
      primaryColor: true,
      accentColor: true,
      backgroundStyle: false,
      heroHeadline: false,
      heroSubheadline: false,
      ctaLabel: false,
      sections: true,
    },

    sectionOptions: [
      "hero",
      "grounds",
      "stats",
      "about",
      "testimonials",
      "faqs",
      "contact",
      "location",
      "app",
    ],

    advancedConfiguration: {
      enabled: true,
      general: false,
      copyFields: Object.freeze([
        "heroBadge",
        "heroTitleLineOne",
        "heroTitleLineTwo",
        "heroTitleHighlight",
        "aboutTitle",
        "contactTitle",
      ]),
      testimonials: true,
      faqs: true,
      stats: false,
      social: true,
      app: true,
    },

    fallbackNotes: {
      advancedConfiguration:
        "Venue name, location, phone, opening hours, facilities, grounds, prices, currency and map use API data where available. Empty optional fields then preserve the exact original Merlion text and bundled assets.",

      copyField:
        "Leave empty to use API-backed venue data where applicable, then the exact original Merlion fallback.",

      testimonials:
        "Uses the original Merlion testimonials when the configured list is empty.",

      faqs:
        "Uses venue phone, hours, grounds and facilities where available, then the original Merlion FAQs.",

      social:
        "Store, social and map links preserve the original layout and use configured URLs when provided.",

      backgroundStyle:
        "Merlion preserves its original warm hardwood, court-crimson and premium-light visual design.",

      sections:
        "Controls the supported Merlion sections without changing their original order.",
    },
  });


const bhaisepatiDefinition =
  createThemeDefinition({
    id: "bhaisepati-sports-academy",
    name: "Bhaisepati Sports Academy",
    description:
      "Premium badminton-academy layout with API-driven courts, venue details and booking actions.",

    defaultPalette: {
      primaryColor: "#FFD700",
      accentColor: "#0A1628",
      backgroundStyle: "clean",
    },

    controls: {
      primaryColor: true,
      accentColor: true,
      backgroundStyle: false,
      heroHeadline: false,
      heroSubheadline: false,
      ctaLabel: false,
      sections: true,
    },

    sectionOptions: [
      "hero",
      "grounds",
      "stats",
      "about",
      "testimonials",
      "faqs",
      "contact",
      "location",
      "app",
    ],

    advancedConfiguration: {
      enabled: true,
      general: false,
      copyFields: Object.freeze([
        "heroBadge",
        "heroTitleLineOne",
        "heroTitleHighlight",
        "aboutTitleLineOne",
        "aboutTitleLineTwo",
        "contactTitle",
      ]),
      testimonials: true,
      faqs: true,
      stats: false,
      social: true,
      app: true,
    },

    fallbackNotes: {
      advancedConfiguration:
        "Venue name, location, phone, opening hours, facilities, grounds, prices, currency and map use API data where available. Empty optional fields then preserve the exact original Bhaisepati Sports Academy text and bundled assets.",

      copyField:
        "Leave empty to use API-backed venue data where applicable, then the exact original Bhaisepati Sports Academy fallback.",

      testimonials:
        "Uses the original Bhaisepati Sports Academy testimonials when the configured list is empty.",

      faqs:
        "Uses venue phone, hours and cancellation policy where available, then the original Bhaisepati Sports Academy FAQs.",

      social:
        "Store, social and map links preserve the original layout and use configured URLs when provided.",

      backgroundStyle:
        "Bhaisepati Sports Academy preserves its original royal-blue, electric-gold athletic visual design.",

      sections:
        "Controls the supported Bhaisepati sections without changing their original order.",
    },
  });


const turfParkDefinition =
  createThemeDefinition({
    id: "turf-park",
    name: "Turf Park",
    description:
      "Premium multi-sport layout with API-driven grounds, venue details, statistics and booking actions.",

    defaultPalette: {
      primaryColor: "#2FE5A7",
      accentColor: "#06090F",
      backgroundStyle: "clean",
    },

    controls: {
      primaryColor: true,
      accentColor: true,
      backgroundStyle: false,
      heroHeadline: false,
      heroSubheadline: false,
      ctaLabel: false,
      sections: true,
    },

    sectionOptions: [
      "hero",
      "grounds",
      {
        key: "about",
        label: "About & Statistics",
      },
      "testimonials",
      "faqs",
      {
        key: "contact",
        label: "Contact & Map",
      },
      "app",
    ],

    advancedConfiguration: {
      enabled: true,
      general: false,
      copyFields: Object.freeze([
        "heroBadge",
        "heroTitleLineOne",
        "heroTitleHighlight",
        "groundsTitle",
        "aboutTitleLineOne",
        "aboutTitleLineTwo",
        "contactTitleLineOne",
        "contactTitleLineTwo",
      ]),
      testimonials: true,
      faqs: true,
      stats: false,
      social: true,
      app: true,
    },

    fallbackNotes: {
      advancedConfiguration:
        "Venue name, location, phone, opening hours, facilities, grounds, prices, currency, statistics and map use API data where available. Empty optional fields then preserve the exact original Turf Park text and bundled assets.",

      copyField:
        "Leave empty to use API-backed venue data where applicable, then the exact original Turf Park fallback.",

      testimonials:
        "Uses the original Turf Park testimonials when the configured list is empty.",

      faqs:
        "Uses venue grounds, phone, hours, facilities and cancellation policy where available, then the original Turf Park FAQs.",

      social:
        "App, social and map links preserve the original layout and use configured URLs when provided.",

      backgroundStyle:
        "Turf Park preserves its original deep-navy and emerald-green premium visual design.",

      sections:
        "Controls the supported Turf Park sections without changing their original order.",
    },
  });


const boxArenaCourtlineDefinition =
  createThemeDefinition({
    id: "boxarena-courtline",
    name: "BoxArena Courtline",
    description:
      "Editorial indoor-sports layout with animated court lines, API-driven venue details, gallery and booking actions.",

    defaultPalette: {
      primaryColor: "#E7A339",
      accentColor: "#101913",
      backgroundStyle: "clean",
    },

    controls: {
      primaryColor: true,
      accentColor: true,
      backgroundStyle: false,
      heroHeadline: false,
      heroSubheadline: false,
      ctaLabel: false,
      sections: true,
    },

    sectionOptions: [
      "hero",
      {
        key: "about",
        label: "About & Gallery",
      },
      "faqs",
      "location",
    ],

    advancedConfiguration: {
      enabled: true,
      general: false,
      copyFields: Object.freeze([
        "heroEyebrow",
        "heroTitleLineOne",
        "heroTitleLineTwo",
        "aboutTitle",
        "galleryTitle",
        "faqTitle",
        "locationTitle",
      ]),
      testimonials: false,
      faqs: true,
      stats: false,
      social: true,
      app: false,
    },

    fallbackNotes: {
      advancedConfiguration:
        "Venue name, address, phone, opening hours, grounds, sports, gallery media, facilities and map use API data where available. Empty optional fields preserve the exact peer BoxArena Courtline text, styling and bundled artwork.",

      copyField:
        "Leave empty to use API-backed venue data where applicable, then the exact original BoxArena Courtline fallback.",

      faqs:
        "Uses the venue facilities and cancellation policy where available, then the original peer FAQs.",

      social:
        "Contact email and map embed are optional overrides; venue phone and location remain API-driven.",

      backgroundStyle:
        "BoxArena Courtline preserves the original ink, turf, chalk, amber and sand design tokens.",

      sections:
        "Controls the supported Courtline sections; About and Gallery remain grouped to preserve the original responsive layout.",
    },
  });


const ACTIVE_THEME_DEFINITIONS =
  Object.freeze([
    arenaDefinition,
    boxArenaDefinition,
    picklePointDefinition,
    merlionDefinition,
    bhaisepatiDefinition,
    turfParkDefinition,
    boxArenaCourtlineDefinition,
  ]);

const definitionList = [
  ...LEGACY_THEME_DEFINITIONS,
  ...ACTIVE_THEME_DEFINITIONS,
];

export const THEME_DEFINITIONS =
  Object.freeze(
    definitionList.reduce(
      (definitions, definition) => {
        definitions[definition.id] =
          definition;

        return definitions;
      },
      {},
    ),
  );

const toThemeOption = (definition) =>
  Object.freeze({
    id: definition.id,
    name: definition.name,
    description: definition.description,
  });

export const THEME_OPTIONS =
  Object.freeze(
    ACTIVE_THEME_DEFINITIONS.map(
      toThemeOption,
    ),
  );

export const SELECTABLE_THEME_LAYOUTS =
  Object.freeze(
    THEME_OPTIONS.map(
      (theme) => theme.id,
    ),
  );

export const WEBSITE_THEME_LAYOUTS =
  Object.freeze(
    definitionList.map(
      (definition) => definition.id,
    ),
  );

export const getThemeDefinition = (
  layout,
) =>
  THEME_DEFINITIONS[layout] ||
  THEME_DEFINITIONS.classic;